import { get, getDatabase, ref, set } from 'firebase/database';
import { requireSuperadmin } from './adminGuard';
import { recordActivity } from './activityApi';
import { ALL_BUILDING_TYPES, BuildingType } from '../types/BuildingTypeEnums';
import {
    ENABLED_BUILDING_TYPES_PATH,
    LEGACY_BUILDING_TYPES_PATH,
    parseEnabledBuildingTypes,
    parseLegacyBuildingTypes,
} from '../types/enabledBuildingTypes';

/**
 * Read once per app session and keep the result: the set changes very rarely
 * and a reload after changing it is fine. `setEnabledBuildingTypes` updates
 * the cached value so the superadmin page reflects the change at once.
 */
let cached: Promise<BuildingType[] | null> | null = null;

export function getEnabledBuildingTypes(): Promise<BuildingType[] | null> {
    if (!cached) {
        cached = get(ref(getDatabase(), ENABLED_BUILDING_TYPES_PATH))
            .then(snapshot => parseEnabledBuildingTypes(snapshot.val()))
            .catch(err => {
                console.error('Failed to read enabled building types', err);
                cached = null;
                return null;
            });
    }
    return cached;
}

export async function getLegacyBuildingTypes(): Promise<BuildingType[] | null> {
    try {
        const snapshot = await get(ref(getDatabase(), LEGACY_BUILDING_TYPES_PATH));
        return parseLegacyBuildingTypes(snapshot.val());
    } catch (err) {
        console.error('Failed to read legacy building types', err);
        return null;
    }
}

export async function setEnabledBuildingTypes(types: BuildingType[]): Promise<void> {
    const db = getDatabase();
    await requireSuperadmin(db, 'ändra byggnadstyper');
    const enabled = ALL_BUILDING_TYPES.filter(type => types.includes(type));
    if (enabled.length === 0) {
        throw new Error('Minst en byggnadstyp måste vara aktiverad');
    }

    const node: Record<string, true> = {};
    enabled.forEach(type => { node[type] = true; });
    try {
        await set(ref(db, ENABLED_BUILDING_TYPES_PATH), node);
        cached = Promise.resolve(enabled);
    } catch (err) {
        console.error('Failed to save enabled building types', err);
        throw new Error('Kunde inte spara byggnadstyperna. Försök igen.');
    }
    recordActivity('settings.updated');
}
